// PenPressureConfig — ToolPanel ペン詳細セクション直下に配置する筆圧設定 UI
// (consult 2026-05-09 / architecture.md §19.15).
//
// 設計の核心:
// - ペン種ごとに表示内容を切替 (= 効果対象が無い toggle は出さない).
// - 設定値は **ClassicLayout 側で localStorage に永続化済**, ここでは props の
//   value + onChange だけ受け取る controlled component.
// - α 期は ON/OFF + min スライダのシンプル版. Modal 化はしない (= 即切替したい
//   ので低頻度切替の color modal とは別軸). β で本格 UI (CSP 風カーブエディタ) を
//   入れる予定 — 受け皿は上部バー「ペン」ボタンの専用モーダル (`PenSettingsModal`、
//   consult 2026-06-13 続報。旧計画の UserSettingsModal「ペン詳細」タブから変更).
// - 旧 `JTabletConfigCanvas` (DSP 64×98 px) のテイストを残しつつ HTML/CSS 流に
//   簡素化. 80px サイドバーに収まるサイズで、各効果は ON/OFF toggle + 横長 min
//   スライダ (= 1500² キャンバス向けに細幅化したペン UI 全体と整合).
//
// **ペン種別の表示内容** (architecture.md §19.15 / penPressure.ts ヘッダ参照、
// consult 2026-05-09 ペン統合):
//
//   | ペン     | 太さ筆圧 | alpha 筆圧 |
//   |---------|---------|-----------|
//   | brush   | toggle  | toggle (旧 normal+water 統合, alpha 軸はペン統合で常に有効) |
//   | flat    | toggle  | **toggle (disabled で表示)** ← 後述 |
//   | eraser  | toggle  | —         |
//
// **`'flat'` の濃さ筆圧 = 操作可能** (Stage Y consult 2026-05-31,
// `marker-pen-deprecation-plan.md` §2 Stage Y / Y-1):
// drawing-core 側 (Y-2 commit 7d6b66b) で `'flat'` の alphaScale が `pt.pAlpha`
// 連動に統合済 (= `'marker'` と同等). default `alphaEnabled=false` で既存ユーザの
// uniform alpha 挙動は **完全互換** (= 明示 ON にしない限り `pAlpha=1.0` 明示で
// drawing-core 側 alphaScale=1.0 固定)、明示 ON でコピックマーカー風挙動 (= max
// 蓄積 + 筆圧連動、stroke 内最大濃さで凍結) が直交軸として得られる. これにより
// 旧 [コピック] (= `'marker'`) ボタンは UI 不要となり `ToolPanel.tsx` の
// `ENABLE_MARKER_PEN=false` で非表示化済.
//
// 旧仕様 (= disabled で表示) は consult 2026-05-09 ユーザ実機 fb で「`'flat'` の
// 本質 = uniform alpha」を二重制御で守るために導入していたが、Stage Y の本質
// 統合により ALPHA_DISABLED は実質的に不要となった (= flat 行は false に倒し、
// `ALPHA_DISABLED` 自体は Stage X で完全撤去予定).
//
// (architecture.md §19.3 / §19.15 / marker-pen-deprecation-plan.md §2 Stage Y)

import type { StrokePen } from '@oekaki/protocol';

import type { PenPressureSettings } from '../../../application/penPressure.js';
import { LegacyGaugeSlider } from './LegacyGaugeSlider.js';
import { outsetFrame } from './legacyFrames.js';

export interface PenPressureConfigProps {
  /** 現選択ペン種. 「太さ」「alpha」 toggle の表示有無を決める. */
  readonly penKind: StrokePen;
  /** 現在の設定値 (caller が localStorage から読んだ値を渡す, controlled). */
  readonly settings: PenPressureSettings;
  /** ON/OFF と min の変更時に発火. caller が localStorage 書き込み + state 更新. */
  readonly onChange: (next: PenPressureSettings) => void;
}

/**
 * 「太さ筆圧」UI を表示するペン種 (consult 2026-05-09 ペン統合).
 *
 * - brush / eraser: 効果対象あり (= toggle 表示).
 * - flat (旧 alpha): 「太さ連動」を入れるかは drawing-engine-developer 側の
 *   最終判断に従う方針 (consult 2026-05-09 残された設計判断). UI には toggle
 *   だけ用意して挙動は drawing 側に従う形が安全と判断、表示する.
 */
const SHOW_WIDTH_TOGGLE: Readonly<Record<StrokePen, boolean>> = {
  brush: true,
  flat: true, // 出すだけ、挙動は drawing 側で決まる
  // 2026-05-09 (consult `'marker'` 試験追加): `'flat'` の max 蓄積モードを
  // 維持しつつ濃さ筆圧連動を default ON にした第 3 のペン. 太さも他ペン同様
  // toggle 表示 + default ON で「ペンタブで描いたら太さ・濃さ両方変わる」UX.
  marker: true,
  eraser: true,
  // texture spike (texture-brush-design.md §8): brush 相当扱い。Classic では
  // テクスチャを UI に出さない (Modern 限定) が、`Record<StrokePen, ...>` 充足のため定義。
  texture: true,
  // スクリーントーン (texture-brush-design.md §12.2 / phase11-light-pens-2026-08-21.md
  // §4.5-§4.7): Classic/Modern 両方に露出済みだが、筆圧グラデは不採用のまま (consult
  // 2026-06-20)。density/pitch は penParams (番手/間隔 UI) で stroke 単位に固定する。
  tone: false,
  // エアブラシ (airbrush-design-2026-08-15.md §2.5): brush/texture と同じ 2 軸筆圧
  // (penPressure.ts の airbrush エントリ参照)。Classic / Modern 両方に露出
  // (§0 表現パリティ原則、texture と異なり Classic 除外の理由が無い)。
  airbrush: true,
  // クレヨン / パステル (phase11-light-pens-2026-08-21.md §2.6): Classic/Modern
  // 両方に露出済みだが、grain segmented (目の粗さ) が既存の調整軸なので筆圧トグルは
  // 出さない。ドット (同 §3.5): 同じく間隔スライダが調整軸なので筆圧トグルは出さない。
  crayon: false,
  pastel: false,
  dot: false,
};

/** 「alpha 筆圧」UI を表示するペン種. effect=「alpha=255 固定」のペンでは隠す. */
const SHOW_ALPHA_TOGGLE: Readonly<Record<StrokePen, boolean>> = {
  // 2026-05-09 (consult ペン統合 / architecture.md §19.3 §19.15): `'brush'` は
  // 旧 normal + water 統合で alpha 軸が常に有効. Step 3 で per-stamp alpha が
  // 累乗カーブ実装後に「不透明線画 + 入り抜き」を alpha 筆圧 ON で得られる.
  brush: true,
  flat: true, // 表示はする (= UI の伸縮を避ける) が ALPHA_DISABLED で disabled に倒す
  // 2026-05-09 (consult `'marker'` 試験追加): `'flat'` と違い濃さ筆圧連動が
  // 有効 (= 本ペンの目玉機能). 表示 ON + ALPHA_DISABLED.marker = false で操作可.
  marker: true,
  eraser: false, // 消ゴムは alpha 筆圧不要 (= 薄く消す機能はない)
  // texture spike: brush 相当扱い (Modern 限定、Classic 非表示)。
  texture: true,
  // スクリーントーン (texture-brush-design.md §12.2): 筆圧グラデ不採用 (露出済みだが
  // 対象外、上部 SHOW_WIDTH_TOGGLE.tone 参照)。
  tone: false,
  // エアブラシ: 濃さ筆圧が本ペンの目玉機能の一部 (太筆圧=ink判定域拡大、
  // 濃筆圧=満額inkのper-stamp alpha上昇、airbrush-design-2026-08-15.md §2.5)。
  airbrush: true,
  // クレヨン / パステル / ドット: 露出済みだが筆圧軸は対象外 (grain/間隔スライダが
  // 調整軸、上部 SHOW_WIDTH_TOGGLE 参照)。
  crayon: false,
  pastel: false,
  dot: false,
};

/**
 * 「alpha 筆圧」UI を **disabled で表示** するペン種 (consult 2026-05-09 ユーザ実機 fb).
 *
 * `SHOW_ALPHA_TOGGLE` (= 表示/非表示) と直交した第 2 の制御軸. 表示するが操作不可
 * (= グレーアウト + checkbox / slider が disabled) にして「このペンでは alpha 筆圧
 * 連動が使えない」を視覚化する.
 *
 * - `flat`: `false` (= 操作可、Stage Y consult 2026-05-31,
 *   `marker-pen-deprecation-plan.md` §2 Stage Y / Y-1). drawing-core 側で
 *   `'flat'` の alphaScale が `pt.pAlpha` 連動に統合済 (= `'marker'` と同等、
 *   Y-2 commit 7d6b66b の `strokeRenderer.ts`). default `alphaEnabled=false` で
 *   既存ユーザの uniform 挙動は完全互換、明示 ON でコピックマーカー風挙動
 *   (= max 蓄積 + 筆圧連動、stroke 内最大濃さで凍結) が得られる. Stage X で
 *   `ALPHA_DISABLED` テーブル自体を撤去予定.
 * - `brush` / `eraser`: `false` (= 通常通り操作可能). `eraser` は SHOW_ALPHA_TOGGLE
 *   が false で行ごと出ないので実質無関係だが、明示性のため定義.
 */
const ALPHA_DISABLED: Readonly<Record<StrokePen, boolean>> = {
  brush: false,
  // Stage Y (consult 2026-05-31): `true` → `false`. drawing-core 統合により
  // 濃さ筆圧連動が意味を持つようになった (= 明示 ON でコピック挙動).
  flat: false,
  // 2026-05-09 (consult `'marker'` 試験追加): `'flat'` と違い濃さ筆圧連動が
  // 仕様上有効 (= max 蓄積 + 濃さ筆圧連動の組合せが本ペンの目玉) なので操作可.
  // Stage Y で `'flat'` 側も操作可になったため `'marker'` は実質 `'flat'` と
  // 等価 (UI 上は `ENABLE_MARKER_PEN=false` で非表示). Stage X で protocol enum
  // 撤去予定.
  marker: false,
  eraser: false,
  // texture spike: brush 相当扱いで操作可 (Classic/Modern 両方に露出、行自体は
  // SHOW_ALPHA_TOGGLE.texture=true で出る)。
  texture: false,
  // スクリーントーン: SHOW_ALPHA_TOGGLE.tone=false で行自体が出ないため実質無関係だが、
  // 明示性のため定義。
  tone: false,
  // エアブラシ: 操作可 (SHOW_ALPHA_TOGGLE.airbrush = true と対で意味あり)。
  airbrush: false,
  // クレヨン / パステル / ドット: SHOW_ALPHA_TOGGLE が false で行自体が出ないため
  // 実質無関係だが、明示性のため定義。
  crayon: false,
  pastel: false,
  dot: false,
};

export const PenPressureConfig = ({
  penKind,
  settings,
  onChange,
}: PenPressureConfigProps): React.ReactNode => {
  const showWidth = SHOW_WIDTH_TOGGLE[penKind];
  const showAlpha = SHOW_ALPHA_TOGGLE[penKind];

  // 全 toggle が無いペン種は枠ごと描画しない (= 視覚的なノイズを避ける).
  if (!showWidth && !showAlpha) return null;

  const onToggleWidth = (): void => {
    onChange({ ...settings, widthEnabled: !settings.widthEnabled });
  };

  const onToggleAlpha = (): void => {
    onChange({ ...settings, alphaEnabled: !settings.alphaEnabled });
  };

  // LegacyGaugeSlider が valueMin..valueMax + step snap 済の値を渡してくれるので
  // ここでの clamp は不要. defensive に Number.isFinite だけチェック.
  const onWidthMinChange = (v: number): void => {
    if (!Number.isFinite(v)) return;
    onChange({ ...settings, widthMin: v });
  };

  const onAlphaMinChange = (v: number): void => {
    if (!Number.isFinite(v)) return;
    onChange({ ...settings, alphaMin: v });
  };

  return (
    <fieldset
      aria-label="筆圧"
      data-manual="draw.pressure"
      className={`flex flex-col gap-0.5 bg-white p-1 text-black ${outsetFrame}`}
    >
      {/* 表示専用ラベル: iPad Apple Pencil ジェスチャによるテキスト選択を抑止
          (consult 2026-05-09). canvas に閉じこもっていないとき (= ボタン操作後に
          focus が外れた状態) で Pencil を canvas 上に運ぶと近接するこのラベル
          要素がハイライトされ pointerleave で描画が中断するため. */}
      <legend className="select-none px-1 text-[10px] font-bold text-black/70">筆圧</legend>
      {showWidth && (
        <PressureRow
          label="太さ"
          enabled={settings.widthEnabled}
          min={settings.widthMin}
          onToggle={onToggleWidth}
          onMinChange={onWidthMinChange}
        />
      )}
      {showAlpha && (
        <PressureRow
          label="濃さ"
          enabled={settings.alphaEnabled}
          min={settings.alphaMin}
          disabled={ALPHA_DISABLED[penKind]}
          onToggle={onToggleAlpha}
          onMinChange={onAlphaMinChange}
        />
      )}
    </fieldset>
  );
};

/**
 * 1 軸 (太さ / 濃さ) の ON/OFF + min スライダの行.
 *
 * - 横 80px サイドバー内に収まるよう checkbox + label + 横長スライダを 縦 2 行に
 *   折る (label + checkbox を 1 行目、スライダを 2 行目).
 * - 2026-05-09 (consult): スライダは旧版風ゲージ式 (`LegacyGaugeSlider`、
 *   `PenWidthSlider` と同じ見た目) に統一. HTML 標準 `<input type="range">` の
 *   モダンな丸 thumb は他の旧版風 UI と合わないため.
 * - OFF 時は LegacyGaugeSlider を disabled に倒す (= 値編集できないことを
 *   視覚的にも明示, 灰色ゲージ + cursor not-allowed).
 *
 * **`disabled` prop** (consult 2026-05-09 ユーザ実機 fb): `'flat'` の濃さ筆圧
 * のように「ペン仕様上 そもそも使えない」軸を表示するが操作不可にするための制御.
 * `enabled` (= ユーザの ON/OFF state) と直交する **第 2 の制御軸** で、`disabled`
 * のときは label 全体グレーアウト + checkbox 操作不可 + LegacyGaugeSlider disabled
 * (= 既存 `!enabled` と OR 結合) に倒す. Stage Y (consult 2026-05-31) で `'flat'`
 * の濃さ筆圧は操作可に転じたため、現状 `disabled=true` で呼ばれる軸はない
 * (= `ALPHA_DISABLED` テーブルは Stage X で完全撤去予定). コードは引き続き残置.
 */
const PressureRow = ({
  label,
  enabled,
  min,
  disabled = false,
  onToggle,
  onMinChange,
}: {
  readonly label: string;
  readonly enabled: boolean;
  readonly min: number;
  readonly disabled?: boolean;
  readonly onToggle: () => void;
  readonly onMinChange: (v: number) => void;
}): React.ReactNode => {
  const id = `pen-pressure-${label}`;
  // 視覚的な「0..100%」表示は廃止 (consult 2026-05-09): 100% (= 3 桁) で
  // 80px サイドバーをはみ出すため. Modern Layout (β 期) の本格 UI で復活想定.
  // スクリーンリーダー読み上げは LegacyGaugeSlider の `ariaValueText` で維持.
  return (
    <div className="flex flex-col gap-0.5">
      {/* 表示専用ラベル領域 (label / 太さ・濃さテキスト) は iPad Apple Pencil
          ジェスチャで選択されると pointerleave で描画が中断するため select-none.
          <input type="checkbox"> の操作性は select-none を継承しても影響なし
          (consult 2026-05-09).
          disabled=true のとき label 全体を半透明 + cursor not-allowed にして
          「このペンでは使えない」を視覚化 (consult 2026-05-09 ユーザ実機 fb). */}
      <label
        htmlFor={`${id}-toggle`}
        className={`flex select-none items-center gap-1 text-[10px] font-bold leading-none ${
          disabled ? 'cursor-not-allowed opacity-50' : ''
        }`}
        aria-disabled={disabled || undefined}
      >
        <input
          id={`${id}-toggle`}
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={onToggle}
          // 標準 checkbox を 12px (text-[10px] と相性◎) で配置.
          // disabled 時は cursor not-allowed + opacity 40 で「操作不可」を視覚化.
          className="h-3 w-3 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`${label}筆圧 ON/OFF`}
        />
        <span>{label}</span>
      </label>
      <LegacyGaugeSlider
        value={min}
        valueMin={0}
        valueMax={1}
        step={0.05}
        // 既存 `!enabled` (= OFF 状態) と新規 `disabled` (= ペン仕様上使えない) の
        // OR 結合. どちらかが true なら slider 操作不可 (= グレー + cursor not-allowed).
        disabled={!enabled || disabled}
        onChange={onMinChange}
        ariaLabel={`${label}筆圧の最低倍率 (0..100%)`}
        ariaValueText={(v) => `${Math.round(v * 100)}%`}
      />
    </div>
  );
};
